// Per-cart-line upsell — minimal JS. The Liquid snippet pre-computes the
// prompt's content + visibility server-side, so the element is visible the
// moment it hits the DOM. This file only handles:
//
//   1. Delegated click on .qb-cart-upsell-button — fires /cart/change.js
//      with the target qty captured in data-qb-target-qty.
//   2. Writes cart.attributes.qb_dow on load so the Rust discount function
//      can gate rules by day-of-week even for customers who never visit a
//      product page.
//
// No hydration, no MutationObserver, no class toggles. Savor's cart
// re-renders the snippet output on every cart update, so the prompt
// updates itself with the new quantity automatically.

(function () {
  if (window.__qbCartUpsellLoaded) return;
  window.__qbCartUpsellLoaded = true;

  /**
   * Shopify's localized cart routes, or an empty object before scripts.liquid has run.
   * `Theme` must be read bare and guarded with typeof — it is a top-level `const` in a
   * classic script, so it lives in the global lexical scope and is never exposed on
   * window, while a bare reference to an undeclared name would throw.
   */
  function qbCartRoutes() {
    return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : window.Theme?.routes || {};
  }

  function getUpsellButton(event) {
    if (event.target && event.target.closest) {
      const button = event.target.closest(".qb-cart-upsell-button");
      if (button) return button;
    }

    if (typeof event.composedPath === "function") {
      return event.composedPath().find((node) => (
        node instanceof HTMLButtonElement && node.classList.contains("qb-cart-upsell-button")
      )) || null;
    }

    return null;
  }

  document.addEventListener("click", async (event) => {
    const btn = getUpsellButton(event);
    if (!btn) return;
    const el = btn.closest(".qb-cart-upsell");
    if (!el) return;
    const lineKey = el.dataset.qbLineKey;
    const targetQty = parseInt(el.dataset.qbTargetQty || "", 10);
    if (!lineKey || !Number.isFinite(targetQty)) return;

    event.preventDefault();
    btn.disabled = true;
    try {
      // Collect every cart-items-component on the page so Shopify renders
      // their sections inline with the change response. Without this the
      // dispatched cart:update event carries empty sections and Savor's
      // cart-items-component falls back to a SECOND fetch (renderSection)
      // — two round-trips per +N click instead of one. Mirrors the pattern
      // in Savor's product-form.js add-to-cart flow.
      const sectionIds = [];
      document.querySelectorAll("cart-items-component").forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });
      const body = { id: lineKey, quantity: targetQty };
      if (sectionIds.length) body.sections = sectionIds.join(",");
      // Theme.routes is localized by Shopify; the literal only holds while the store is
      // served from the root. Read `Theme` bare, not `window.Theme`: scripts.liquid
      // declares it as a top-level `const` in a classic script, which lands in the global
      // lexical scope and never becomes a window property, so window.Theme is always
      // undefined. Mirrors getRoutes() in product-card-add.js.
      const changeUrl = qbCartRoutes().cart_change_url || "/cart/change.js";
      const resp = await fetch(changeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        btn.disabled = false;
        return;
      }
      const cart = await resp.json();
      // Fan out to the rest of the theme so the cart bubble + cart drawer
      // sections update. Savor's cart-items-component picks up `sections`
      // out of detail.data and re-renders, which re-runs our Liquid snippet
      // with the new item.quantity — the upsell prompt updates itself.
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: "qb-cart-upsell",
            data: {
              itemCount: cart.item_count,
              source: "qb-cart-upsell",
              ...(cart.sections ? { sections: cart.sections } : {}),
            },
          },
        }),
      );
    } catch {
      btn.disabled = false;
    }
  }, { capture: true });

  // Day-of-week cart attribute. Browser-local Date.getDay() (0=Sun..6=Sat),
  // matched against config.schedule.daysOfWeek inside the Rust function.
  // Single-store retail in Turkey → browser-local is close enough to
  // shop-local; the edge case is rare and the worst outcome is a customer
  // getting/not-getting a discount near a day boundary.
  if (typeof window.qbWriteDayOfWeekAttribute === "function") {
    window.qbWriteDayOfWeekAttribute();
  } else {
    const today = String(new Date().getDay());
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ attributes: { qb_dow: today } }),
    }).catch(() => {});
  }
})();
